import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import { listAnalyses } from "../lib/api";
import { TID } from "../lib/testIds";
import { ArrowRight, FileSearch, Loader2 } from "lucide-react";

function StatusPill({ status, score }) {
  if (status === "processing")
    return <span className="font-mono text-[10px] tracking-widest uppercase text-sky-400 inline-flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Processing</span>;
  if (status === "failed")
    return <span className="font-mono text-[10px] tracking-widest uppercase text-rose-400">Failed</span>;
  return <span className="metric-number text-sky-400">{score ?? "—"}<span className="text-slate-500 text-xs">/100</span></span>;
}

export default function Recent() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    listAnalyses().then(setItems).catch(() => setItems([]));
  }, []);

  return (
    <div className="grain min-h-screen">
      <Navbar />
      <div className="max-w-[1200px] mx-auto px-6 py-12">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
          <div>
            <div className="tick-label text-sky-400 mb-2">/ Archive</div>
            <h1 className="font-display font-black text-3xl text-slate-100">Recent Analyses</h1>
          </div>
          <Link
            to="/run-analysis"
            className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-400 text-slate-950 font-mono text-[11px] tracking-[0.2em] uppercase font-bold px-5 py-3"
          >
            Run New Analysis <ArrowRight className="w-4 h-4" strokeWidth={3} />
          </Link>
        </div>

        {items === null && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 text-sky-400 animate-spin" />
          </div>
        )}

        {items && items.length === 0 && (
          <div className="panel p-16 text-center">
            <FileSearch className="w-10 h-10 text-slate-600 mx-auto mb-4" />
            <div className="text-slate-300 mb-2">No analyses yet</div>
            <p className="text-sm text-slate-500 max-w-md mx-auto">
              Upload your first solicitation to generate a GovBidIQ Opportunity Intelligence Report.
            </p>
          </div>
        )}

        {items && items.length > 0 && (
          <div className="panel">
            <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-[var(--border-soft)] tick-label">
              <div className="col-span-5">Opportunity / Document</div>
              <div className="col-span-3">Company</div>
              <div className="col-span-2">Verdict</div>
              <div className="col-span-2 text-right">Score</div>
            </div>
            {items.map((it) => (
              <Link
                to={`/report/${it.id}`}
                key={it.id}
                data-testid={TID.recentItem(it.id)}
                className="grid grid-cols-12 gap-4 px-5 py-4 border-b border-[var(--border-soft)] last:border-0 hover:bg-[var(--bg-secondary)] transition-colors group"
              >
                <div className="col-span-5">
                  <div className="text-slate-100 text-sm group-hover:text-sky-400 transition-colors">
                    {it.opportunity_name || it.document_name || "Untitled"}
                  </div>
                  <div className="font-mono text-[10px] text-slate-500 mt-1">{it.created_at?.slice(0, 19).replace("T", " ")}</div>
                </div>
                <div className="col-span-3 text-sm text-slate-300">{it.company_name || "—"}</div>
                <div className="col-span-2 text-sm">
                  {it.final_verdict ? (
                    <span className="font-mono text-[10px] tracking-widest uppercase text-slate-300">{it.final_verdict}</span>
                  ) : (
                    <span className="text-slate-600">—</span>
                  )}
                </div>
                <div className="col-span-2 text-right">
                  <StatusPill status={it.status} score={it.overall_score} />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
